import { z } from 'zod'

import { calendarDate, isoDateTime } from '../lib/date'
import { invoiceLineItemSchema } from './invoice'
import { paymentMethodSchema } from './payment-method'

/**
 * Currently-purchasable membership plan cadences. This is what the
 * plan picker offers, what the checkout schema accepts, and what a
 * new pet can be signed up on:
 *
 * - `monthly`     - billed every month (12 month commitment)
 * - `annual`      - billed every 12 months
 * - `two_yearly`  - billed every 24 months
 *
 * Cadences are named by their literal duration rather than Latin
 * prefixes. `biennial` (24 months) and `biannual` (6 months) are
 * near-homographs that mean very different things, and `biannual` is
 * routinely used for both in ordinary English - so the correct word
 * couldn't disambiguate itself. `two_yearly` and `six_monthly` can't
 * be misread.
 *
 * Retired cadences (`quarterly`, `six_monthly`) live in
 * `legacySubscriptionPlanSchema`. Callers that may receive either
 * (a raw wire value, a URL fragment, etc.) should `safeParse`
 * against `subscriptionPlanSchema` first and fall back to
 * `legacySubscriptionPlanSchema` rather than expressing the union
 * as a single alias - keeping the two schemas distinct means the
 * checkout path can never accidentally accept a legacy value.
 */
export const subscriptionPlanSchema = z.enum(['monthly', 'annual', 'two_yearly'])
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>
export const SUBSCRIPTION_PLANS = subscriptionPlanSchema.options

/**
 * Cadences we no longer sell but existing pets may still be
 * reported on. Kept separate from `subscriptionPlanSchema` so the
 * checkout path can never accept them - any URL that carries one of
 * these as a `:plan` param falls through to the picker fallback.
 */
export const legacySubscriptionPlanSchema = z.enum(['quarterly', 'six_monthly'])
export type LegacySubscriptionPlan = z.infer<typeof legacySubscriptionPlanSchema>
export const LEGACY_SUBSCRIPTION_PLANS = legacySubscriptionPlanSchema.options

/** How many months one billing period of each plan covers. */
export const PLAN_DURATION_MONTHS: Record<SubscriptionPlan | LegacySubscriptionPlan, number> = {
  monthly: 1,
  quarterly: 3,
  six_monthly: 6,
  annual: 12,
  two_yearly: 24,
}

/** Inclusions a plan can advertise; the portal owns each one's copy. */
export const planPerkSchema = z.enum(['wellness_screen'])
export type PlanPerk = z.infer<typeof planPerkSchema>

/** Display label for every plan cadence, current and legacy. */
export const SUBSCRIPTION_PLAN_LABELS: Record<SubscriptionPlan | LegacySubscriptionPlan, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  six_monthly: 'Six-monthly',
  annual: 'Annual',
  two_yearly: 'Two-yearly',
}

/**
 * Which pricing a membership is on, read from its price's lookup key:
 *
 * - `regular` - the generation we sell today
 * - `legacy`  - a price superseded by a rise
 * - `remote`  - a remote-only plan
 */
export const subscriptionPlanTypeSchema = z.enum(['regular', 'legacy', 'remote'])
export type SubscriptionPlanType = z.infer<typeof subscriptionPlanTypeSchema>

/**
 * Stripe's `Subscription.Status` union, kept local so the shared package doesn't depend on the
 * Stripe SDK, plus `scheduled`: a membership booked to start on a future day, which Stripe holds
 * as a subscription schedule until then.
 */
export const subscriptionStatusSchema = z.enum([
  'scheduled',
  'active',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'past_due',
  'paused',
  'trialing',
  'unpaid',
])
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>

export const TERMINATED_SUBSCRIPTION_STATUSES: Set<string> = new Set([
  'canceled',
  'incomplete_expired',
])

export const HEALTHY_SUBSCRIPTION_STATUSES: Set<string> = new Set(['active', 'trialing'])

/**
 * A single addon item attached to a subscription (e.g. flea & worming).
 * `interval` is left as the raw Stripe string to accommodate whatever
 * cadence Stripe reports without forcing us to enumerate them.
 */
export const subscriptionAddonSchema = z.object({
  priceId: z.string(),
  name: z.string(),
  amount: z.number().int(),
  currency: z.string(),
  interval: z.string(),
  intervalCount: z.number().int(),
  currentPeriodStart: isoDateTime.nullable(),
  currentPeriodEnd: isoDateTime.nullable(),
})

export type SubscriptionAddon = z.output<typeof subscriptionAddonSchema>

/**
 * The full subscription payload we project from Stripe. Shared across
 * every surface that needs live subscription state (OS patients,
 * portal, integrations) so all consumers see the same shape.
 *
 * Portal-facing surfaces further project this down to
 * `PublicSubscription` to drop staff-only fields.
 */
export const subscriptionSchema = z.object({
  stripeCustomerId: z.string(),
  paymentMethod: paymentMethodSchema.nullable(),
  status: subscriptionStatusSchema,
  /**
   * Canonical plan cadence for the subscription's primary item. Derived
   * from `price.metadata.plan` where available, otherwise inferred from
   * `interval` + `intervalCount`. `null` when the price is missing or
   * doesn't map to any known cadence - callers should handle that
   * defensively (e.g. show an "Unknown plan" fallback).
   */
  plan: z.union([subscriptionPlanSchema, legacySubscriptionPlanSchema]).nullable(),
  interval: z.string().nullable(),
  intervalCount: z.number().int().nullable(),
  amount: z.number().int().nullable(),
  currency: z.string().nullable(),
  startDate: isoDateTime.nullable(),
  /** The membership's first day. Always set for a `scheduled` membership: the day it starts. */
  effectiveStartDate: calendarDate.nullable(),
  cancelAt: isoDateTime.nullable(),
  endedAt: isoDateTime.nullable(),
  currentPeriodStart: isoDateTime.nullable(),
  currentPeriodEnd: isoDateTime.nullable(),
  addons: z.array(subscriptionAddonSchema),
  /**
   * Includes flea, worm or tick cover from addons, legacy bundles or a subscription referenced by fleaAndWormingSubscriptionId metadata.
   * Temporary compatibility field until subscription repairs make addons fully represent plan coverage.
   */
  hasFleaWormTickPlan: z.boolean(),
  /** Stripe lookup_key of the primary price; what the backend fee rules (`lib/procedure-fees`) read to price a procedure. */
  priceLookupKey: z.string().nullable(),
  /** `null` when the lookup key isn't a membership plan we recognise. */
  planType: subscriptionPlanTypeSchema.nullable(),
  /**
   * Higher-tier cadences available from this plan. This is a cadence check only
   * - it doesn't cover the multi-pet and legacy-bundled cases that the upgrade
   * endpoints reject, so a listed plan can still be refused with an explanation.
   */
  availableUpgrades: z.array(subscriptionPlanSchema),
  /**
   * True when Stripe is holding an item change until its invoice is paid.
   * Set by upgrades made with `pending_if_incomplete`, where the subscription
   * keeps reporting the old plan until the charge settles.
   */
  pendingUpdate: z.boolean(),
})

export type Subscription = z.output<typeof subscriptionSchema>

export const subscriptionCancellationPolicySchema = z.enum([
  'standard',
  'no_usage',
  'one_appointment_only',
])
export type SubscriptionCancellationPolicy = z.output<typeof subscriptionCancellationPolicySchema>

/**
 * One upgrade target with the proration Stripe would charge to switch to it
 * right now. Served by both the member portal and the staff upgrade flows.
 */
/** A coupon taking money off a bill, named as the member sees it. */
export const appliedDiscountSchema = z.object({
  couponId: z.string(),
  name: z.string(),
})

export type AppliedDiscount = z.output<typeof appliedDiscountSchema>

export const upgradeOptionSchema = z.object({
  plan: subscriptionPlanSchema,
  /** Amount in smallest currency unit that Stripe will charge immediately. */
  amount: z.number().int(),
  /** The new plan's price for a full period, after the subscription's discounts. */
  planAmount: z.number().int(),
  currency: z.string(),
  lineItems: z.array(invoiceLineItemSchema),
  /** The subscription's coupons that carry over to this plan. */
  discounts: z.array(appliedDiscountSchema),
  newPeriodEnd: isoDateTime,
})

export type UpgradeOption = z.output<typeof upgradeOptionSchema>

/** Stripe-computed upgrade choices for a subscription's current plan. */
export const subscriptionUpgradePreviewSchema = z.object({
  currentPlan: z.union([subscriptionPlanSchema, legacySubscriptionPlanSchema]),
  options: z.array(upgradeOptionSchema),
})

export type SubscriptionUpgradePreview = z.output<typeof subscriptionUpgradePreviewSchema>
